import { call, put, select } from 'redux-saga/effects';
import i18n from 'i18n';
import { notification } from 'features/notifications/toast/notification';
import { initVirtualBackground } from 'features/virtual-backgrounds/thunks/initVirtualBackground';
import {
  selectActiveVirtualBackground,
  selectVirtualBackgroundSyncConfig,
} from 'features/virtual-backgrounds/selectors';
import {
  virtualBackgroundActivationFailed,
  virtualBackgroundSelectionDropped,
} from 'features/virtual-backgrounds/virtualBackgroundsSlice';
import {
  SelectedVirtualBackground,
  VirtualBackgroundSyncConfig,
} from 'features/virtual-backgrounds/types';
import { isValidVBSelection } from 'utils/types';
import { eventBus } from 'utils/eventBus';

type UnsupportedTrigger =
  | ReturnType<typeof initVirtualBackground.rejected>
  | ReturnType<typeof virtualBackgroundActivationFailed>;

export function* onVirtualBackgroundUnsupportedSaga(action: UnsupportedTrigger) {
  const fromActivation = virtualBackgroundActivationFailed.match(action);
  const reason = fromActivation ? action.payload.reason : action.payload;

  if (reason !== 'unsupported') {
    return;
  }

  // Reducers run first, and activationFailed has already reset `selected` — so the attempt
  // itself is the intent signal for that action, and the SDK watcher reports it from the payload.
  if (!fromActivation) {
    const selected: SelectedVirtualBackground = yield select(selectActiveVirtualBackground);
    const syncConfig: VirtualBackgroundSyncConfig = yield select(selectVirtualBackgroundSyncConfig);

    if (selected.value === '' && !isValidVBSelection(syncConfig)) {
      return;
    }

    // Intent is enough to toast, but the integrator event carries a config — a selection with a
    // value and no usable type is intent without a payload, so it toasts and stays silent.
    const requested = selected.value ? selected : syncConfig;

    if (isValidVBSelection(requested)) {
      yield call(eventBus.virtualBackgroundNotApplied, requested, 'unsupported');
    }
  }

  yield put(virtualBackgroundSelectionDropped());
  yield call(notification, i18n.t('notifications:virtual_bg.unsupported'));
}
